import { normalizeCountryLabel } from "../../../../_shared/_logic/countries"

export const DEFAULT_ORGANIZATION_TIMEZONE = "Europe/Berlin"

export const dateOnlyInTimezone = (instant: Date, timezone: string): string | null => {
  if (!timezone?.trim() || !Number.isFinite(instant.getTime())) return null

  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    const parts = Object.fromEntries(
      formatter.formatToParts(instant).map((part) => [part.type, part.value]),
    )

    return `${parts.year}-${parts.month}-${parts.day}`
  } catch {
    return null
  }
}

const COUNTRY_TIMEZONES: Record<string, string> = {
  Deutschland: "Europe/Berlin",
  Österreich: "Europe/Vienna",
  Schweiz: "Europe/Zurich",
}

export const timezoneForCountry = (country: string | null | undefined): string =>
  COUNTRY_TIMEZONES[normalizeCountryLabel(country)] ?? DEFAULT_ORGANIZATION_TIMEZONE

export const normalizeOrganizationTimezone = (
  timezone: string | null | undefined,
  country: string | null | undefined,
): string => {
  const trimmed = timezone?.trim()

  return trimmed || timezoneForCountry(country)
}

export const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/

export type DateOnlyInput = string | Date | number | null | undefined

const padDatePart = (value: number) => `${value}`.padStart(2, "0")

const dateFromDateOnlyParts = (year: number, month: number, day: number): Date | null => {
  if (![year, month, day].every(Number.isFinite)) return null

  const parsed = new Date(year, month - 1, day)

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }

  return parsed
}

export const parseDateOnly = (value: DateOnlyInput): Date | null => {
  if (value === null || value === undefined || value === "") return null

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null

    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }

  if (typeof value === "string") {
    const trimmed = value.trim()

    if (!trimmed) return null

    if (DATE_ONLY_REGEX.test(trimmed)) {
      const [year, month, day] = trimmed.split("-").map(Number)

      return dateFromDateOnlyParts(year, month, day)
    }

    const parsed = new Date(trimmed)

    if (Number.isNaN(parsed.getTime())) return null

    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return null

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

export const formatDateOnly = (value: DateOnlyInput): string => {
  if (typeof value === "string" && DATE_ONLY_REGEX.test(value.trim())) {
    return parseDateOnly(value) ? value.trim() : ""
  }

  const parsed = parseDateOnly(value)

  if (!parsed) return ""

  return `${parsed.getFullYear()}-${padDatePart(parsed.getMonth() + 1)}-${padDatePart(parsed.getDate())}`
}

export const normalizeDateOnly = (value: DateOnlyInput): string | null =>
  formatDateOnly(value) || null

export const localDateWithOffset = (daysFromToday: number, base = new Date()): string => {
  const local = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  local.setDate(local.getDate() + daysFromToday)

  return formatDateOnly(local)
}

export const formatDateOnlyDisplay = (value: DateOnlyInput, fallback = "–"): string => {
  const parsed = parseDateOnly(value)

  if (!parsed) return fallback

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed)
}

export const dateOnlyTimestamp = (value: DateOnlyInput): number | null => {
  const parsed = parseDateOnly(value)

  return parsed ? parsed.getTime() : null
}
